import React, { useState, useEffect, useCallback } from "react";
import { adminAxios } from "./adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { RefreshCw, Trash2, Phone, MapPin, CalendarDays, Clock, Users, CheckCircle2, Hourglass, XCircle, IndianRupee, Send, Search, MessageCircle, List, LayoutGrid, ChevronLeft, ChevronRight, Receipt, Download } from "lucide-react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from "date-fns";

const statusStyles = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-blue-100 text-blue-700",
  assigned: "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600",
};
const STATUSES = ["pending", "confirmed", "assigned", "completed", "cancelled"];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm p-5 flex items-center gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
    <div><div className="text-2xl font-extrabold text-gray-900">{value}</div><div className="text-xs text-gray-500">{label}</div></div>
  </div>
);

const BookingsPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [beauticians, setBeauticians] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, assigned: 0, completed: 0, cancelled: 0, revenue: 0 });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(format(new Date(), "yyyy-MM-dd"));
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const [b, s, bt] = await Promise.all([
        adminAxios().get(`/bookings${q}`),
        adminAxios().get(`/bookings/stats`),
        adminAxios().get(`/admin/beauticians`),
      ]);
      setBookings(b.data); setStats(s.data); setBeauticians(bt.data);
    } catch (e) { toast({ title: "Failed to load" }); } finally { setLoading(false); }
  }, [filter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try { await adminAxios().patch(`/bookings/${id}`, { status }); toast({ title: `Marked ${status}` }); load(); }
    catch { toast({ title: "Update failed" }); }
  };

  const assign = async (booking, beautician_id) => {
    try {
      await adminAxios().patch(`/bookings/${booking.id}/assign`, { beautician_id });
      const bt = beauticians.find((x) => x.id === beautician_id);
      toast({ title: `Assigned to ${bt?.name}`, description: "Click 'Send WhatsApp' to notify." });
      load();
    } catch { toast({ title: "Assign failed" }); }
  };

  const sendWhatsApp = (b) => {
    const bt = beauticians.find((x) => x.id === b.assigned_beautician_id);
    if (!bt || !bt.phone) { toast({ title: "Assign a beautician with a phone number first" }); return; }
    const items = (b.items || []).map((i) => `• ${i.name} (₹${i.price})`).join("\n");
    const msg = `*New Booking Assigned* 💇‍♀️\n\n*Customer:* ${b.customer_name}\n*Phone:* ${b.phone}\n*Date:* ${b.date} at ${b.time}\n*Location:* ${b.city}${b.address ? ", " + b.address : ""}\n\n*Services:*\n${items}\n\n*Total:* ₹${b.total}`;
    window.open(`https://wa.me/${bt.phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const notifyCustomer = (b, type) => {
    const phone = (b.phone || "").replace(/\D/g, "");
    if (!phone) { toast({ title: "No customer phone number" }); return; }
    const waPhone = phone.length === 10 ? `91${phone}` : phone;
    const items = (b.items || []).map((i) => i.name).join(", ");
    let msg;
    if (type === "assigned" && b.assigned_beautician_name) {
      msg = `Hi ${b.customer_name}! 💖 Your BeBlushify beautician *${b.assigned_beautician_name}* has been assigned for your booking on ${b.date} at ${b.time}. Services: ${items}. She will reach ${b.city}. See you soon!`;
    } else {
      msg = `Hi ${b.customer_name}! 💖 Your BeBlushify booking is confirmed for ${b.date} at ${b.time}. Services: ${items}. Total: ₹${b.total} (${(b.payment_method || "pay_at_service").replace(/_/g, " ")}). Thank you for choosing BeBlushify!`;
    }
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const remove = async (id) => {
    try { await adminAxios().delete(`/bookings/${id}`); toast({ title: "Deleted" }); load(); } catch { toast({ title: "Delete failed" }); }
  };

  const invoiceUrl = (b) => `${window.location.origin}/invoice/${b.id}`;
  const downloadBill = (b) => window.open(invoiceUrl(b), "_blank");
  const sendBill = (b) => {
    const phone = (b.phone || "").replace(/\D/g, "");
    if (!phone) { toast({ title: "No customer phone number" }); return; }
    const waPhone = phone.length === 10 ? `91${phone}` : phone;
    const items = (b.items || []).map((i) => `• ${i.name} — ₹${i.price}`).join("\n");
    const msg = `Hi ${b.customer_name}! 💖 Thank you for choosing BeBlushify.\n\n*Your Bill*\n${items}\n\n*Total Paid: ₹${b.total}*\n\nView & download your bill here:\n${invoiceUrl(b)}`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? bookings.filter((b) =>
        (b.customer_name || "").toLowerCase().includes(q) ||
        (b.phone || "").toLowerCase().includes(q) ||
        (b.items || []).some((i) => (i.name || "").toLowerCase().includes(q)) ||
        (b.assigned_beautician_name || "").toLowerCase().includes(q))
    : bookings;

  const byDate = {};
  filtered.forEach((b) => { if (b.date) { (byDate[b.date] = byDate[b.date] || []).push(b); } });
  const monthStart = startOfMonth(calMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(calMonth), { weekStartsOn: 1 });
  const calDays = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const dayBookings = selectedDay ? (byDate[selectedDay] || []) : [];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Total" value={stats.total} color="bg-primary/10 text-primary" />
        <StatCard icon={Hourglass} label="Pending" value={stats.pending} color="bg-amber-100 text-amber-600" />
        <StatCard icon={CalendarDays} label="Confirmed" value={stats.confirmed} color="bg-blue-100 text-blue-600" />
        <StatCard icon={Users} label="Assigned" value={stats.assigned} color="bg-purple-100 text-purple-600" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} color="bg-green-100 text-green-600" />
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${stats.revenue}`} color="bg-emerald-100 text-emerald-600" />
      </div>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-white ring-1 ring-black/5">
            <TabsTrigger value="all">All</TabsTrigger>
            {STATUSES.map((s) => <TabsTrigger key={s} value={s} className="capitalize">{s}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white ring-1 ring-black/5 rounded-lg p-0.5" data-testid="bookings-view-toggle">
            <button onClick={() => setView("list")} data-testid="view-list-btn" className={`flex items-center gap-1.5 px-3 h-8 rounded-md text-sm font-medium transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-800"}`}><List className="w-4 h-4" /> List</button>
            <button onClick={() => setView("calendar")} data-testid="view-calendar-btn" className={`flex items-center gap-1.5 px-3 h-8 rounded-md text-sm font-medium transition-colors ${view === "calendar" ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-800"}`}><LayoutGrid className="w-4 h-4" /> Calendar</button>
          </div>
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 h-9">
            <Search className="w-4 h-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, service..." className="outline-none text-sm w-48 md:w-64" />
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </div>


      {view === "list" && (
      <div className="mt-4 bg-white rounded-2xl ring-1 ring-black/5 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-gray-400"><CalendarDays className="w-10 h-10 mx-auto opacity-40" /><p className="mt-3">No bookings {search ? "match your search" : filter !== "all" ? `(${filter})` : "yet"}.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/40">
                <TableHead>Customer</TableHead><TableHead>Services</TableHead><TableHead>Schedule</TableHead>
                <TableHead>Amount</TableHead><TableHead>Beautician</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell><div className="font-medium text-gray-900">{b.customer_name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {b.phone}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 max-w-[160px]"><MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{b.city}{b.address ? `, ${b.address}` : ""}</span></div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[200px]">{(b.items || []).map((i) => i.name).join(", ")}</TableCell>
                    <TableCell className="text-sm text-gray-600"><div className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {b.date || "—"}</div><div className="flex items-center gap-1 mt-0.5"><Clock className="w-3.5 h-3.5" /> {b.time || "—"}</div></TableCell>
                    <TableCell className="text-sm"><div className="font-bold text-gray-900">₹{b.total}</div>{b.discount > 0 && <div className="text-xs text-green-600">-₹{b.discount} offer</div>}{b.coins_redeemed > 0 && <div className="text-xs text-amber-600">-{b.coins_redeemed} coins</div>}<div className="text-[11px] text-gray-400 capitalize mt-0.5">{(b.payment_method || "pay_at_service").replace(/_/g, " ")}</div></TableCell>
                    <TableCell>
                      <Select value={b.assigned_beautician_id || ""} onValueChange={(v) => assign(b, v)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue placeholder="Assign..." /></SelectTrigger>
                        <SelectContent>{beauticians.map((bt) => <SelectItem key={bt.id} value={bt.id}>{bt.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="flex flex-col gap-0.5 mt-1">
                        {b.assigned_beautician_id && <button className="flex items-center text-xs text-green-600 hover:text-green-700" onClick={() => sendWhatsApp(b)}><Send className="w-3.5 h-3.5 mr-1" /> Notify Beautician</button>}
                        <button className="flex items-center text-xs text-blue-600 hover:text-blue-700" onClick={() => notifyCustomer(b, b.assigned_beautician_name ? "assigned" : "confirmed")}><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp Customer</button>
                        {b.status === "completed" && <>
                          <button className="flex items-center text-xs text-primary hover:text-primary/80" data-testid={`send-bill-${b.id}`} onClick={() => sendBill(b)}><Receipt className="w-3.5 h-3.5 mr-1" /> Send Bill</button>
                          <button className="flex items-center text-xs text-gray-600 hover:text-gray-800" data-testid={`download-bill-${b.id}`} onClick={() => downloadBill(b)}><Download className="w-3.5 h-3.5 mr-1" /> Download Bill</button>
                        </>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                        <SelectTrigger className="w-[120px] h-8 border-0 p-0 focus:ring-0"><Badge className={`capitalize ${statusStyles[b.status]}`}>{b.status}</Badge></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete booking?</AlertDialogTitle><AlertDialogDescription>Remove {b.customer_name}'s booking permanently.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => remove(b.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      )}

      {view === "calendar" && (
      <div className="mt-4 grid lg:grid-cols-[1fr_360px] gap-4" data-testid="bookings-calendar">
        <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCalMonth(subMonths(calMonth, 1))} data-testid="cal-prev" className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <div className="font-bold text-gray-900">{format(calMonth, "MMMM yyyy")}</div>
            <button onClick={() => setCalMonth(addMonths(calMonth, 1))} data-testid="cal-next" className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-400 mb-1">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calDays.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const count = (byDate[key] || []).length;
              const inMonth = isSameMonth(d, calMonth);
              const isToday = isSameDay(d, new Date());
              const isSel = selectedDay === key;
              return (
                <button key={key} data-testid={`cal-day-${key}`} onClick={() => setSelectedDay(key)}
                  className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-colors
                    ${isSel ? "bg-primary text-primary-foreground" : inMonth ? "hover:bg-secondary text-gray-800" : "text-gray-300"}
                    ${isToday && !isSel ? "ring-1 ring-primary" : ""}`}>
                  <span>{format(d, "d")}</span>
                  {count > 0 && <span className={`mt-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${isSel ? "bg-white/25 text-white" : "bg-primary/10 text-primary"}`}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b">
            <CalendarDays className="w-4 h-4 text-primary" />
            <div className="font-bold text-gray-900">{selectedDay ? format(parseISO(selectedDay), "EEE, dd MMM yyyy") : "Select a day"}</div>
            <Badge className="ml-auto bg-primary/10 text-primary">{dayBookings.length}</Badge>
          </div>
          {dayBookings.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm"><CalendarDays className="w-8 h-8 mx-auto opacity-40" /><p className="mt-2">No bookings this day.</p></div>
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
              {dayBookings.slice().sort((a, b) => (a.time || "").localeCompare(b.time || "")).map((b) => (
                <div key={b.id} className="rounded-xl ring-1 ring-black/5 p-3" data-testid={`day-booking-${b.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{b.customer_name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {b.time || "—"}</div>
                    </div>
                    <Badge className={`capitalize ${statusStyles[b.status]}`}>{b.status}</Badge>
                  </div>
                  <div className="text-xs text-gray-600 mt-1.5">{(b.items || []).map((i) => i.name).join(", ")}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {b.phone} · ₹{b.total}</div>
                  {b.assigned_beautician_name && <div className="text-xs text-purple-600 mt-1">Beautician: {b.assigned_beautician_name}</div>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                    <button className="flex items-center text-xs text-blue-600 hover:text-blue-700" onClick={() => notifyCustomer(b, b.assigned_beautician_name ? "assigned" : "confirmed")}><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp Customer</button>
                    {b.status === "completed" && <>
                      <button className="flex items-center text-xs text-primary hover:text-primary/80" onClick={() => sendBill(b)}><Receipt className="w-3.5 h-3.5 mr-1" /> Send Bill</button>
                      <button className="flex items-center text-xs text-gray-600 hover:text-gray-800" onClick={() => downloadBill(b)}><Download className="w-3.5 h-3.5 mr-1" /> Download Bill</button>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default BookingsPanel;
